import {
  ExclamationTriangleIcon,
  PencilIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { State } from "../../../Models";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Loading from "../../Shared/Loading";
import { useNotification } from "../../../context/NotificationContext";
import { isQuarantineCity } from "../../../Helpers";
import { QuarantineDataType } from "../Quarantine";
import { getAlternateNameSuggestions, updateCityState } from "../../../api/quarantine.api";

export default function MajorCityConfirmationPopup({
  size,
  isOpen,
  onCancel,
  isModalConfirm,
  selectedRows,
  states,
  updateSelectedRows,
  gptCityDetails,
  token
}: {
  onCancel: Function;
  isOpen: boolean;
  isModalConfirm: Function;
  size: string;
  states: State[];
  selectedRows: QuarantineDataType[];
  updateSelectedRows: Function;
  gptCityDetails: any;
  token: string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const cancelButtonRef = useRef(null);
  const [replaceCommon, setReplaceCommon] = useState<boolean[]>([]);
  const { success, error: notifyError } = useNotification();
  const [originalStateIds, setOriginalStateIds] = useState<string[]>([]);
  
  // Per-row alternate names (independent of edit mode)
  const [alternateNames, setAlternateNames] = useState<string[]>([]);

  // Per-row loading state for AI suggestions
  const [suggestionLoading, setSuggestionLoading] = useState<boolean[]>([]);

  // State for editing functionality (name + state only)
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [editedName, setEditedName] = useState<string>("");
  const [editedState, setEditedState] = useState<State | null>(null);
  const [gptApplied, setGptApplied] = useState<boolean>(false);

  // Initialize states based on selectedRows length (once per open)
  useEffect(() => {
    if (!open) return;
    setReplaceCommon(new Array(selectedRows.length).fill(true));
    setAlternateNames(selectedRows.map((row) => row.alternateNames || ""));
    setSuggestionLoading(new Array(selectedRows.length).fill(false));
  }, [open, selectedRows.length]);

  // Sync open state with isOpen prop
  useEffect(() => {
    setOpen(isOpen);
    if (!isOpen) {
      setGptApplied(false);
      setOriginalStateIds([]);
    }
    if (isOpen) {
      setOriginalStateIds(selectedRows.map((row) => row.state.id));
    }
  }, [isOpen]);

  // Auto-fill from GPT details (or backend actiongpt) if available.
  // Guarded to only update rows when the values actually differ to avoid render loops.
  useEffect(() => {
    if (!open || gptApplied) return;

    selectedRows.forEach((row, index) => {
      const cityDetails =
        (gptCityDetails?.[row.id] && gptCityDetails[row.id].corrected_name
          ? gptCityDetails[row.id]
          : (row as any).actiongpt) || null;

      if (!cityDetails?.corrected_name) return;

      const nextName = cityDetails.corrected_name as string;
      const nextAlt = alternateNames[index] || "";
      const currentAlt = row.alternateNames || "";

      // Only push an update if something actually changes
      if (row.name !== nextName || currentAlt !== nextAlt) {
        updateSelectedRows(index, nextName, row.state.id, nextAlt);
      }
    });

    setGptApplied(true);
  }, [open, gptApplied, gptCityDetails, selectedRows, alternateNames, updateSelectedRows]);

  const hasAnyStateCorrection = selectedRows.some((row, index) => {
    const details = gptCityDetails?.[row.id] || (row as any).actiongpt;
    const correctedStateId = details?.correctedStateId;
    const originalId = originalStateIds[index];
    return !!correctedStateId && !!originalId && correctedStateId !== originalId;
  });

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleCancel = () => {
    onCancel();
    setOpen(false);
  };

  const handleConfirm = () => {
    // Build updated rows from what is currently shown in the popup (including any unsaved edits)
    const updatedRows = selectedRows.map((row, index) => {
      const baseState = states.find((s) => s.id === row.state.id) || row.state;
      // If this row is in edit mode, prefer the in-modal edited values
      if (index === editingIndex && editedState) {
        return {
          ...row,
          name: editedName,
          state: editedState,
          alternateNames: alternateNames[index] || "",
        };
      }

      return {
        ...row,
        name: row.name,
        state: baseState,
        alternateNames: alternateNames[index] || "",
      };
    });

    // Push the final values back to parent so table state matches the popup
    updatedRows.forEach((row, index) => {
      updateSelectedRows(
        index,
        row.name,
        row.state.id,
        row.alternateNames || ""
      );
    });

    // Pass updated rows directly to handler
    isModalConfirm(replaceCommon, updatedRows);
    setOpen(false);
  };

  const handleReplaceCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setReplaceCommon((prevState) => {
      const newState = [...prevState];
      newState[index] = event.target.checked;
      return newState;
    });
  };

  const handleAlternateNameChange = (index: number, value: string) => {
    setAlternateNames((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // AI Suggestion handler
  const handleGetSuggestions = async (index: number) => {
    const row = selectedRows[index];
    const cityName = getDisplayName(index);
    const stateName = row.state?.name || "";

    if (!cityName) {
      notifyError("Error", "City name is required");
      return;
    }

    setSuggestionLoading((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });

    try {
      const response = await getAlternateNameSuggestions(cityName, stateName, token);

      if (response && response.alternateNames) {
        const suggestions = response.alternateNames;
        const existingNames = alternateNames[index]
          ? alternateNames[index].split(",").map((n) => n.trim().toLowerCase())
          : [];

        // Filter out duplicates and merge
        const newNames = suggestions.filter(
          (name: string) => !existingNames.includes(name.toLowerCase())
        );

        const merged = alternateNames[index]
          ? `${alternateNames[index]}, ${newNames.join(", ")}`
          : newNames.join(", ");

        setAlternateNames((prev) => {
          const next = [...prev];
          next[index] = merged.replace(/^,\s*/, "").replace(/,\s*$/, "");
          return next;
        });

        success(
          "Suggestions Added",
          `${newNames.length} alternate name(s) suggested`
        );
      } else {
        notifyError("No Suggestions", "No alternate names found for this city");
      }
    } catch (error: any) {
      notifyError(
        "Error",
        error.message || "Failed to get alternate name suggestions"
      );
    } finally {
      setSuggestionLoading((prev) => {
        const next = [...prev];
        next[index] = false;
        return next;
      });
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    const row = selectedRows[index];
    const cityDetails =
      (gptCityDetails?.[row.id] && gptCityDetails[row.id].corrected_name
        ? gptCityDetails[row.id]
        : (row as any).actiongpt) || null;
    setEditedName(cityDetails?.corrected_name || row.name);
    setEditedState(selectedRows[index].state);
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateId = e.target.value;
    const state = states.find((s) => s.id === stateId);
    if (state) setEditedState(state);
  };

  const handleSave = async () => {
    if (editingIndex === -1 || !editedState) return;
    setLoading(true);
    try {
      updateSelectedRows(
        editingIndex,
        editedName,
        editedState.id,
        alternateNames[editingIndex] || ""
      );
      success("Success", "City updated successfully");
      setEditingIndex(-1);
    } catch (error) {
      notifyError("Error", "Failed to update city");
    } finally {
      setLoading(false);
    }
  };

  const cancelEditing = () => {
    setEditingIndex(-1);
    setEditedName("");
    setEditedState(null);
  };

  const getDisplayName = useCallback(
    (index: number) => {
      const row = selectedRows[index];
      const cityDetails =
        (gptCityDetails?.[row.id] && gptCityDetails[row.id].corrected_name
          ? gptCityDetails[row.id]
          : (row as any).actiongpt) || null;
      return cityDetails?.corrected_name || row.name;
    },
    [gptCityDetails, selectedRows]
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={handleCancel}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-darkbg dark:text-white shadow-xl transition-all w-full max-w-5xl mx-auto">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                    <div className="text-lg ml-2 font-semibold">
                      Are you sure? You want to confirm the selected cities as
                      major cities?
                    </div>
                  </div>

                  {hasAnyStateCorrection && (
                    <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200 flex items-start gap-2">
                      <ExclamationTriangleIcon className="h-5 w-5 mt-0.5" />
                      <span>
                        For one or more cities, GPT suggests a different state than the one currently stored.
                        Please review the suggested states below and update if needed.
                      </span>
                    </div>
                  )}

                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    Total Cities: {selectedRows.length}
                  </p>

                  <div className="mt-2">
                    <ul className="overflow-y-auto max-h-80 pr-4 rounded-md bg-gray-100 dark:bg-darkbglight dark:text-gray-400">
                      {selectedRows.map((column, index) => (
                        <div
                          key={column.id}
                          className="flex flex-col border-b border-gray-200 last:border-b-0 py-2"
                        >
                          {/* Row 1: City name, state, edit button, replace checkbox */}
                          <div className="flex flex-row items-center justify-between">
                            {editingIndex === index ? (
                              <li className="pl-4 py-1 font-semibold flex flex-row items-center gap-2 flex-wrap">
                                <span>{`${index + 1}.`}</span>
                                <input
                                  type="text"
                                  value={editedName}
                                  onChange={(e) =>
                                    setEditedName(e.target.value)
                                  }
                                  className="border rounded px-2 py-1"
                                  placeholder="City name"
                                />
                                <select
                                  value={editedState?.id || ""}
                                  onChange={handleStateChange}
                                  className="border rounded px-2 py-1 w-[200px]"
                                >
                                  <option value="">Select State</option>
                                  {states.map((state) => (
                                    <option key={state.id} value={state.id}>
                                      {state.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={handleSave}
                                  className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                                  disabled={loading}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="bg-gray-300 text-gray-700 px-2 py-1 rounded hover:bg-gray-400"
                                  disabled={loading}
                                >
                                  Cancel
                                </button>
                              </li>
                            ) : (
                              <li className="pl-4 py-1 font-semibold flex flex-col items-start gap-1">
                                <span className="flex flex-row items-center gap-2">
                                  <span>{`${index + 1}. ${getDisplayName(index)}`}</span>
                                  <span className="text-gray-500">
                                    {column.state.name}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <PencilIcon
                                      height={12}
                                      className="hover:cursor-pointer text-blue-500"
                                      onClick={() => startEditing(index)}
                                    />
                                    {(() => {
                                      const details =
                                        gptCityDetails?.[column.id] ||
                                        (column as any).actiongpt;
                                      const correctedStateId =
                                        details?.correctedStateId as string | undefined;
                                      const originalId = originalStateIds[index];

                                      if (
                                        !correctedStateId ||
                                        !originalId ||
                                        correctedStateId === originalId
                                      ) {
                                        return null;
                                      }

                                      return (
                                        <ExclamationTriangleIcon
                                          className="w-4 h-4 text-yellow-500"
                                          title="GPT suggests a different state for this city"
                                        />
                                      );
                                    })()}
                                  </div>
                                </span>
                                {(() => {
                                  const details =
                                    gptCityDetails?.[column.id] ||
                                    (column as any).actiongpt;
                                  const correctedStateId =
                                    details?.correctedStateId as string | undefined;
                                  const originalId = originalStateIds[index];
                                  const correctedState =
                                    correctedStateId &&
                                    states.find((s) => s.id === correctedStateId);

                                  if (
                                    !correctedStateId ||
                                    !correctedState ||
                                    !originalId ||
                                    correctedStateId === originalId
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <span className="text-xs font-light text-grey-700">
                                      GPT suggests this city may belong to{" "}
                                      <span className="font-semibold">
                                        {correctedState.name}
                                      </span>{" "}
                                      instead of{" "}
                                      <span className="font-semibold">
                                        {column.state.name}
                                      </span>
                                      . Please review and update the state if needed.
                                    </span>
                                  );
                                })()}
                              </li>
                            )}

                            {editingIndex === -1 &&
                              isQuarantineCity(column) &&
                              (column as any).replacedCities &&
                              (column as any).replacedCities?.length > 0 && (
                                <li className="pl-4 py-1 font-semibold flex items-center">
                                  <label htmlFor={`replaceCity-major-${index}`}>
                                    Also update{" "}
                                    {(column as any).replacedCities?.length}{" "}
                                    common cities
                                  </label>
                                  <input
                                    type="checkbox"
                                    id={`replaceCity-major-${index}`}
                                    className="ml-2 rounded z-10 text-indigo-600"
                                    checked={replaceCommon[index]}
                                    onChange={(e) =>
                                      handleReplaceCheckbox(e, index)
                                    }
                                  />
                                </li>
                              )}
                          </div>

                          {/* Row 2: Alternate names field with AI suggestion button */}
                          <div className="pl-8 pt-1 flex items-center gap-2">
                            <label className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              Alternate names:
                            </label>
                            <input
                              type="text"
                              value={alternateNames[index] || ""}
                              onChange={(e) =>
                                handleAlternateNameChange(index, e.target.value)
                              }
                              className="border rounded px-2 py-1 text-sm w-72 placeholder:text-gray-400"
                              placeholder="Enter alternate names separated by comma"
                            />
                            <button
                              onClick={() => handleGetSuggestions(index)}
                              disabled={suggestionLoading[index]}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Get AI suggestions for alternate names"
                            >
                              {suggestionLoading[index] ? (
                                <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                              ) : (
                                <SparklesIcon className="w-4 h-4" />
                              )}
                              Suggest
                            </button>
                            {alternateNames[index] && (
                              <span className="text-green-600 text-xs">
                                ✔{" "}
                                {
                                  alternateNames[index]
                                    .split(",")
                                    .filter(Boolean).length
                                }{" "}
                                alternate name(s)
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </ul>
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      className="bg-gray-200 text-gray-700 px-4 py-2 mr-2 rounded hover:bg-gray-400"
                      onClick={handleCancel}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      onClick={handleConfirm}
                    >
                      Confirm
                    </button>
                  </div>
                </div>

                {loading && <Loading />}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}